import { defineStore, acceptHMRUpdate } from 'pinia'
import { useMaps } from './maps'
import { useTasksStore } from './tasks'
import router from '@/router'
import { toRaw } from 'vue'
import { useDrawingStore } from './drawing'
import { useFieldsConnectionStore } from './fieldsConnection'
import { useSegmentsStore } from './segments'
import { useCustomers } from './customers'
import { useParkingLotsStore } from './parkingLots'

export const useFieldLayersStore = defineStore('fieldLayers', {
    state: () => ({
        layers: [
            {
                id: 'field',
                label: 'Field',
                visible: true,
                color: null,
                originalColor: null,
                originalVisibility: true,
            },
            {
                id: 'outer_border',
                label: 'Outer border',
                visible: true,
                color: null,
                originalColor: null,
                originalVisibility: true,
            },
            {
                id: 'headland',
                label: 'Headland',
                visible: true,
                color: null,
                originalColor: null,
                originalVisibility: true,
            },
            {
                id: 'ab_lines',
                label: 'AB lines',
                visible: true,
                color: null,
                originalColor: null,
                originalVisibility: true,
            },
            {
                id: 'segments',
                label: 'Segments',
                visible: true,
                color: null,
                originalColor: null,
                originalVisibility: true,
            },
            {
                id: 'track_wheels',
                label: 'Wheels track',
                visible: false,
                color: '#00FFFF',
                originalColor: '#00FFFF',
                originalVisibility: false,
            },
             // ROI Layers
            {
                id: 'roi_entrypoints',
                label: 'Zones: Entry lines',
                visible: true,
                color: null,
                originalColor: null,
                originalVisibility: false,
            },
            {
                id: 'roi_obstacles',
                label: 'Zones: Obstacles',
                visible: true,
                color: null,
                originalColor: null,
                originalVisibility: false,
            },
            {
                id: 'roi_logistics',
                label: 'Zones: Logistics',
                visible: true,
                color: null,
                originalColor: null,
                originalVisibility: false,
            },
            {
                id: 'cutout',
                label: 'Zones: Cutouts',
                visible: true,
                color: null,
                originalColor: null,
                originalVisibility: false,
            },
            {
                id: 'farm_roi',
                label: 'All farm zones',
                visible: false,
                color: null,
                originalColor: null,
                originalVisibility: false,
            },
            {
                id: 'parking_lots',
                label: 'Parkings',
                visible: false,
                color: null,
                originalColor: null,
                originalVisibility: false,
            }
        ],
        showColorPicker: {
            field: false,
            outer_border: false,
            headland: false,
            inner_border: false,
            ab_lines: false,
            track_wheels: false,
            segments: false,
            roi_entrypoints: false,
            roi_obstacles: false,
            roi_logistics: false,
            cutout: false,
            farm_roi: false,
            parking_lots: false,
        },
        hasField: false,
        shouldShowControl: false, // state to track if control should be visible
        newLayers: null, // used for drawing mode (create or edit)
        singleFieldLayers: null, // used for fields-connection single mode
        field1Layers: null, // used for fields-connection double mode
        field2Layers: null, // used for fields-connection double mode
        roiSubLayerPairs: [
            ['roi_entrypoints', 'entrypoint'],
            ['roi_obstacles', 'obstacle'],
            ['roi_logistics', 'logistic'],
            ['cutout', 'cutout'],
        ],
    }),
    getters: {
        // Return a specific layer by ID
        getLayer: (state) => (id) => {
            return state.layers.find((layer) => layer.id === id)
        },

        // Check if a ROI layer has any ROIs of the required type
        hasROIsForLayer: () => (layerId) => {
            const fcs = useFieldsConnectionStore()
            const fls = useFieldLayersStore()
            
            if (!fcs.generatedROIs || fcs.generatedROIs.length === 0 || !fcs.geojsonResponse) {
                // Update layer visibility to false if no ROIs
                const layer = fls.layers.find(l => l.id === layerId)
                if (layer) {
                    layer.visible = false
                }
                return false
            }
            
            // Check if the layer has any ROIs
            const hasROIs = fcs.generatedROIs.some((roi) => {
                const rawROI = toRaw(roi)
                
                if (layerId === 'roi_entrypoints') {
                    return rawROI.roiType === 'entrypoint'
                } else if (layerId === 'roi_obstacles') {
                    return rawROI.roiType === 'obstacle'
                } else if (layerId === 'roi_logistics') {
                    return rawROI.roiType === 'logistic'
                } else if (layerId === 'cutout') {
                    return rawROI.roiType === 'cutout'
                }
                
                return false
            })
            
            // Update layer visibility based on hasROIs
            const layer = fls.layers.find(l => l.id === layerId)
            if (layer && !hasROIs) {
                layer.visible = false
            }
            
            return hasROIs
        },
    },
    actions: {
        // Check route and decide if control should be visible
        async checkRouteForVisibility() {
            const routeName = router.currentRoute.value.name
            const isDrawingNamedRoute = routeName === 'drawing' || routeName === 'drawing-customer'
            const ds = useDrawingStore()
            const mapStore = useMaps()

            // return always a boolean value
            const hasFieldId = Boolean(
                router.currentRoute.value.params.id || 
                router.currentRoute.value.params.fieldId ||
                (isDrawingNamedRoute && ds.field?.id)
            )

            const isFieldsConnectionRoute = routeName === 'fields-connection' || routeName === 'fields-connection-customer'
            const isDrawingRoute = Boolean(isDrawingNamedRoute && !ds.field.id)

            this.shouldShowControl = hasFieldId || isFieldsConnectionRoute || isDrawingRoute

            if (hasFieldId) {
                // For field route, initialize the layers
                this.initializeLayers()
                this.hasField = true
            } else if (isDrawingRoute) {
                // For drawing route (create or edit), initialize the NEW layers
                await mapStore.hideNavField()
                await mapStore.showAllBorders()
                this.initializeNewLayers()
                this.hasField = false
            } else {
                // For other routes, clear field overlays and show all fields again
                mapStore.hideParkingLotsLayer()
                await mapStore.hideNavField()
                this.restoreOriginalSettings()
                await mapStore.showAllBorders()
                this.hasField = false
            }
        },
        // Initialize the NEW layers for edit/create
        initializeNewLayers() {
            const ds = useDrawingStore()
            const segmentsStore = useSegmentsStore()
            const cs = useCustomers()
            const pls = useParkingLotsStore()
            
            // Check if the data is available for each layer
            const hasShapes = ds.field.shapes && ds.field.shapes.length > 0
            const hasHeadland = ds.headlandPolygons && ds.headlandPolygons.length > 0
            const hasAbLines = (ds.abLinesPolylines && ds.abLinesPolylines.length > 0)
            const hasSegments = (segmentsStore.allSegmentsLines && segmentsStore.allSegmentsLines.length > 0)
            const hasTrackWheels = ds.wheelTrackLinesPolylines && ds.wheelTrackLinesPolylines.length > 0
            
            // Clone the existing layers for NEW with visible: false for layers that don't have data
            if (!this.newLayers) {
                this.newLayers = this.layers.map(layer => {
                    let visible = false // default false for all
                    let dataAvailable = false
                    
                    // Set visible if the data is available
                    switch (layer.id) {
                        case 'field':
                            visible = hasShapes
                            dataAvailable = hasShapes
                            break
                        case 'outer_border':
                            visible = hasShapes
                            dataAvailable = hasShapes
                            break
                        case 'headland':
                            visible = false // Headland is default OFF, can be activated manually
                            dataAvailable = hasHeadland
                            break
                        case 'ab_lines':
                            visible = hasAbLines
                            dataAvailable = hasAbLines
                            break
                        case 'segments':
                            visible = hasSegments
                            dataAvailable = hasSegments
                            break
                        case 'track_wheels':
                            visible = false // Always start hidden, user can enable manually
                            dataAvailable = hasTrackWheels
                            break
                        default:
                            visible = false
                            dataAvailable = false
                    }
                    
                    return {
                        ...layer,
                        id: layer.id,
                        label: layer.label,
                        visible: visible,
                        color: layer.color,
                        originalColor: layer.originalColor,
                        originalVisibility: layer.originalVisibility,
                        dataAvailable: dataAvailable, // Track if the data is available
                    }
                })
            } else {
                // Update visible for the existing layers if the data is available
                this.newLayers.forEach(layer => {
                    const previouslyHadData = layer.dataAvailable || false
                    let nowHasData = false
                    
                    switch (layer.id) {
                        case 'field':
                            nowHasData = hasShapes
                            if (!nowHasData) {
                                layer.visible = false
                            } else if (!previouslyHadData && nowHasData) {
                                layer.visible = true
                            }
                            break
                        case 'outer_border':
                            nowHasData = hasShapes
                            if (!nowHasData) {
                                layer.visible = false
                            } else if (!previouslyHadData && nowHasData) {
                                layer.visible = true
                            }
                            break
                        case 'headland':
                            nowHasData = hasHeadland
                            // Headland remains OFF by default
                            if (!nowHasData) {
                                layer.visible = false
                            }
                            break
                        case 'ab_lines':
                            nowHasData = hasAbLines
                            if (!nowHasData) {
                                layer.visible = false
                            } else if (!previouslyHadData && nowHasData) {
                                layer.visible = true
                            }
                            break
                        case 'segments':
                            nowHasData = hasSegments
                            if (!nowHasData) {
                                layer.visible = false
                            } else if (!previouslyHadData && nowHasData) {
                                layer.visible = true
                            }
                            break
                        case 'track_wheels':
                            nowHasData = hasTrackWheels
                            // Hidden by default, user can enable manually
                            if (!nowHasData) {
                                layer.visible = false
                            }
                            break
                        case 'farm_roi':
                            nowHasData = cs.selectedCustomers.length === 1
                            if (!nowHasData) {
                                layer.visible = false
                            }
                            break
                        case 'parking_lots':
                            nowHasData = cs.selectedCustomers.length === 1
                            if (!nowHasData) {
                                layer.visible = false
                            }
                            break
                    }
                    // Save the current state for comparison at the next update
                    layer.dataAvailable = nowHasData
                })
            }

            // Update colors from drawingStore for layers that have data
            // For headland
            if (hasHeadland) {
                const headlandLayer = this.newLayers.find((l) => l.id === 'headland')
                if (headlandLayer) {
                    const firstHeadland = ds.headlandPolygons.find(item => item.type === 'headland')
                    if (firstHeadland) {
                        const polygon = toRaw(firstHeadland.polygon)
                        const currentColor = polygon.strokeColor || 'transparent'
                        headlandLayer.color = currentColor || '#40E0D0' // fallback to default color
                        headlandLayer.originalColor = headlandLayer.color
                    }
                }
            }
            // For AB lines
            if (ds.abLinesPolylines && ds.abLinesPolylines.length > 0) {
                const abLinesLayer = this.newLayers.find((l) => l.id === 'ab_lines')
                if (abLinesLayer) {
                    const firstPolyline = toRaw(ds.abLinesPolylines[0])
                    const currentColor = firstPolyline.strokeColor || '#00ff00'
                    abLinesLayer.color = currentColor
                    abLinesLayer.originalColor = currentColor
                }
            }
            // For segments
            const segmentsLayer = this.newLayers.find((l) => l.id === 'segments')
            if (segmentsLayer) {
                if (segmentsStore.allSegmentsLines && segmentsStore.allSegmentsLines.length > 0) {
                    const firstSegment = toRaw(segmentsStore.allSegmentsLines[0])
                    const currentColor = firstSegment.strokeColor || '#40E0D0'
                    segmentsLayer.color = currentColor
                    segmentsLayer.originalColor = currentColor
                } 
            }
            // For track_wheels
            if (hasTrackWheels) {
                const trackWheelsLayer = this.newLayers.find((l) => l.id === 'track_wheels')
                if (trackWheelsLayer) {
                    const firstPolyline = toRaw(ds.wheelTrackLinesPolylines[0])
                    const currentColor = firstPolyline.strokeColor || '#FFFF00'
                    trackWheelsLayer.color = currentColor
                    trackWheelsLayer.originalColor = currentColor
                }
            }
            // For outer border
            if (hasShapes) {
                const outerBorderLayer = this.newLayers.find((l) => l.id === 'outer_border')
                if (outerBorderLayer) {
                    const shape = ds.field.shapes[0]
                    const currentColor = shape.strokeColor || '#fb00f6'
                    outerBorderLayer.color = currentColor
                    outerBorderLayer.originalColor = currentColor
                }
                // For field polygon
                const fieldLayer = this.newLayers.find((l) => l.id === 'field')
                if (fieldLayer) {
                    const shape = ds.field.shapes[0]
                    const currentColor = shape.fillColor || '#FF0000'
                    fieldLayer.color = currentColor
                    fieldLayer.originalColor = currentColor
                }
            }
        },
        // Initialize layer visibility and colors from the field
        initializeLayers() {
            const taskStore = useTasksStore()
            const mapStore = useMaps()
            
            if (!taskStore.field) {
                this.hasField = false
                return
            }

            this.hasField = true

            // Store original colors and visibility state for field
            if (taskStore.field.poly) {
                const fieldLayer = this.layers.find((l) => l.id === 'field')
                if (fieldLayer) {
                    const currentColor = taskStore.field.poly.fillColor || taskStore.field.color || '#4a235a'
                    fieldLayer.color = currentColor
                    fieldLayer.originalColor = currentColor
                    // fieldLayer.visible = taskStore.field.poly.getVisible() !== false
                    fieldLayer.visible = fieldLayer.visible !== false
                    fieldLayer.originalVisibility = fieldLayer.visible
                }
            }
            // For outer border - this is part of the field polygon's stroke in most cases
            // since outer_border is a GeoJSON object, not a Google Maps object
            const outerBorderLayer = this.layers.find((l) => l.id === 'outer_border')
            if (outerBorderLayer) {
                let currentColor, isVisible

                if (taskStore.field.poly) {
                    currentColor = taskStore.field.poly.strokeColor || '#2d5a27'
                    isVisible = taskStore.field.poly.strokeOpacity !== 0
                } else {
                    currentColor = '#2d5a27'
                    isVisible = true
                }

                outerBorderLayer.color = currentColor
                outerBorderLayer.originalColor = currentColor
                outerBorderLayer.visible = isVisible
                outerBorderLayer.originalVisibility = isVisible
            }
            // For headland
            if (mapStore.draw.field.headlanes?.length > 0) {
                const headlandLayer = this.layers.find((l) => l.id === 'headland')
                if (headlandLayer) {
                    headlandLayer.color = mapStore.draw.field.headlanes[0].strokeColor || '#ff0000'
                    headlandLayer.originalColor = headlandLayer.color
                    headlandLayer.visible = mapStore.draw.field.headlanes[0].getVisible?.() !== false
                    headlandLayer.originalVisibility = headlandLayer.visible
                }
            }
            // For AB lines
            if (mapStore.draw.field.abLines?.length > 0) {
                const abLinesLayer = this.layers.find((l) => l.id === 'ab_lines')
                if (abLinesLayer) {
                    abLinesLayer.color = mapStore.draw.field.abLines[0].strokeColor || '#00ff00'
                    abLinesLayer.originalColor = abLinesLayer.color
                    abLinesLayer.visible = mapStore.draw.field.abLines[0].getVisible?.() !== false
                    abLinesLayer.originalVisibility = abLinesLayer.visible
                }
            }
            // For segments
            if (mapStore.draw.field.segments?.length > 0) {
                const segmentsLayer = this.layers.find((l) => l.id === 'segments')
                if (segmentsLayer) {
                    segmentsLayer.visible = mapStore.draw.field.segments[0].getVisible?.() !== false
                    segmentsLayer.originalVisibility = segmentsLayer.visible
                }
            }
            // For track wheels (uses icons strokeOpacity, not getVisible)
            if (mapStore.draw.field.trackWheels?.length > 0) {
                const trackWheelsLayer = this.layers.find((l) => l.id === 'track_wheels')
                if (trackWheelsLayer) {
                    const icons = mapStore.draw.field.trackWheels[0].get?.('icons') || mapStore.draw.field.trackWheels[0].icons
                    trackWheelsLayer.visible = icons?.[0]?.icon?.strokeOpacity > 0
                    trackWheelsLayer.originalVisibility = trackWheelsLayer.visible
                }
            }
            // Initialize other layers with default colors if they don't exist yet
            this.layers.forEach((layer) => {
                if (layer.color === null) {
                    switch (layer.id) {
                        case 'track_wheels':
                            layer.color = '#00FFFF'
                            layer.originalColor = '#00FFFF'
                            break
                        case 'segments':
                            layer.color = '#ff7f00'
                            layer.originalColor = '#ff7f00'
                            break
                        case 'ab_lines':
                            layer.color = '#00ff00'
                            layer.originalColor = '#00ff00'
                            break
                        case 'headland':
                            layer.color = '#FFFF00'
                            layer.originalColor = '#FFFF00'
                            break
                        // Set defaults for any other undefined layers
                        default:
                            if (layer.color === null) {
                                layer.color = '#000000'
                                layer.originalColor = '#000000'
                            }
                    }
                }
            })
        },
        // Toggle layer visibility
        toggleLayerVisibility(layer) {
            const taskStore = useTasksStore()
            const mapStore = useMaps()
            const fcs = useFieldsConnectionStore()
            const cs = useCustomers()
            const pls = useParkingLotsStore()
            const routeName = router.currentRoute.value.name

            // For fields-connection, we don't have taskStore.field
            const isFieldsConnectionRoute = routeName === 'fields-connection' || routeName === 'fields-connection-customer'
            const farmId = cs.selectedCustomers.length === 1 ? cs.selectedCustomers[0].id : null
            
            if (!taskStore.field && !isFieldsConnectionRoute && layer.id !== 'farm_roi' && layer.id !== 'parking_lots') return

            // If layer is being hidden, close the color picker
            if (!layer.visible) {
                this.showColorPicker[layer.id] = false
            }

            switch (layer.id) {
                case 'field': {
                    if (isFieldsConnectionRoute) {
                        // For fields-connection, apply to all selected fields
                        if (fcs.startField_id) {
                            const startField = mapStore.fields.find(f => f.id === fcs.startField_id)
                            if (startField && startField.poly) {
                                toRaw(startField.poly).setOptions({
                                    fillOpacity: layer.visible ? 0.5 : 0,
                                })
                            }
                        }
                        if (fcs.endField_id) {
                            const endField = mapStore.fields.find(f => f.id === fcs.endField_id)
                            if (endField && endField.poly) {
                                toRaw(endField.poly).setOptions({
                                    fillOpacity: layer.visible ? 0.5 : 0,
                                })
                            }
                        }
                    } else {
                        // Use the polygon from mapStore.fields
                        const mapField = mapStore.fields.find(f => f.id === taskStore.field.id)
                        
                        if (mapField && mapField.poly) {
                            toRaw(mapField.poly).setOptions({
                                fillOpacity: layer.visible ? 0.5 : 0,
                            })
                        }
                    }
                    break
                }
                case 'outer_border': {
                    if (isFieldsConnectionRoute) {
                        // For fields-connection, apply to all selected fields
                        if (fcs.startField_id) {
                            const startField = mapStore.fields.find(f => f.id === fcs.startField_id)
                            if (startField && startField.poly) {
                                toRaw(startField.poly).setOptions({
                                    strokeOpacity: layer.visible ? 1 : 0,
                                })
                            }
                        }
                        if (fcs.endField_id) {
                            const endField = mapStore.fields.find(f => f.id === fcs.endField_id)
                            if (endField && endField.poly) {
                                toRaw(endField.poly).setOptions({
                                    strokeOpacity: layer.visible ? 1 : 0,
                                })
                            }
                        }
                    } else {
                        // Use the polygon from mapStore.fields
                        const mapField = mapStore.fields.find(f => f.id === taskStore.field.id)
                        
                        if (mapField && mapField.poly) {
                            toRaw(mapField.poly).setOptions({
                                strokeOpacity: layer.visible ? 1 : 0,
                            })
                        }
                    }
                    break
                }
                case 'headland':
                    if (mapStore.draw.field.headlanes && mapStore.draw.field.headlanes.length > 0) {
                        toRaw(mapStore.draw.field.headlanes).forEach((line) => {
                            line.setVisible(layer.visible)
                        })
                    }
                    break
                case 'ab_lines':
                    if (mapStore.draw.field.abLines && mapStore.draw.field.abLines.length > 0) {
                        toRaw(mapStore.draw.field.abLines).forEach((line) => {
                            line.setVisible(layer.visible)
                        })
                    }
                    break
                case 'segments':
                    if (mapStore.draw.field.segments && mapStore.draw.field.segments.length > 0) {
                        toRaw(mapStore.draw.field.segments).forEach((segment) => {
                            segment.setVisible(layer.visible)
                        })
                    }
                    break
                case 'track_wheels':
                    if (mapStore.draw.field.trackWheels && mapStore.draw.field.trackWheels.length > 0) {
                        mapStore.draw.field.trackWheels.forEach((polyline) => {
                            toRaw(polyline).setOptions({
                                icons: [{
                                    icon: {
                                        path: 'M 0,0 0,1',
                                        strokeOpacity: layer.visible ? 1 : 0,
                                        scale: 4
                                    },
                                    offset: '0',
                                    repeat: '20px'
                                }]
                            })
                        })
                    }
                    break
                // ROI Layers
                case 'roi_entrypoints':
                    if (!fcs.generatedROIs) return

                    fcs.generatedROIs.forEach((roi) => {
                        const rawROI = toRaw(roi)
                        if (rawROI.roiType === 'entrypoint') {
                            if (!layer.visible) fcs.closeInfoWindowsForROI(rawROI)
                            // Polyline uses setVisible; AdvancedMarkerElement uses setMap / map
                            if (typeof rawROI.setVisible === 'function') {
                                rawROI.setVisible(layer.visible)
                            } else if (typeof rawROI.setMap === 'function') {
                                rawROI.setMap(layer.visible ? toRaw(mapStore.map) : null)
                            } else {
                                rawROI.map = layer.visible ? toRaw(mapStore.map) : null
                            }
                        }
                    })
                    break   
                case 'roi_obstacles':
                    if (!fcs.generatedROIs) return

                    fcs.generatedROIs.forEach((roi) => {
                        const rawROI = toRaw(roi)
                        if (rawROI.roiType === 'obstacle') {
                            if (!layer.visible) fcs.closeInfoWindowsForROI(rawROI)
                            // Polygon uses setVisible, AdvancedMarkerElement uses setMap
                            if (typeof rawROI.setVisible === 'function') {
                                rawROI.setVisible(layer.visible)
                            } else if (typeof rawROI.setMap === 'function') {
                                rawROI.setMap(layer.visible ? toRaw(mapStore.map) : null)
                            } else {
                                rawROI.map = layer.visible ? toRaw(mapStore.map) : null
                            }
                        }
                    })
                    break
                case 'roi_logistics':
                    if (!fcs.generatedROIs) return

                    fcs.generatedROIs.forEach((roi) => {
                        const rawROI = toRaw(roi)
                        if (rawROI.roiType === 'logistic') {
                            if (!layer.visible) fcs.closeInfoWindowsForROI(rawROI)
                            if (typeof rawROI.setMap === 'function') {
                                rawROI.setMap(layer.visible ? toRaw(mapStore.map) : null)
                            } else {
                                rawROI.map = layer.visible ? toRaw(mapStore.map) : null
                            }
                        }
                    })
                    break
                case 'cutout':
                    if (!fcs.generatedROIs) return
                    fcs.generatedROIs.forEach((roi) => {
                        const rawROI = toRaw(roi)
                        if (rawROI.roiType === 'cutout') {
                            if (!layer.visible) fcs.closeInfoWindowsForROI(rawROI)
                            // Polygon uses setVisible, AdvancedMarkerElement uses setMap
                            if (typeof rawROI.setVisible === 'function') {
                                rawROI.setVisible(layer.visible)
                            } else if (typeof rawROI.setMap === 'function') {
                                rawROI.setMap(layer.visible ? toRaw(mapStore.map) : null)
                            } else {
                                rawROI.map = layer.visible ? toRaw(mapStore.map) : null
                            }
                        }
                    })
                    break
                case 'farm_roi':
                    if (!farmId) return

                    if (layer.visible) {
                        fcs.getFarmROIs(farmId)
                    } else {
                        fcs.clearFarmROIs(farmId)
                    }
                    break
                case 'parking_lots':
                    if (layer.visible) {
                        if (!farmId) return
                        pls.loadParkingLots(farmId)
                    } else {
                        pls.clearParkingLots()
                    }
                    break
            }
        },
        // Update layer color
        updateLayerColor(layer, color, colorChanged = true) {
            const taskStore = useTasksStore()
            const mapStore = useMaps()
            const fcs = useFieldsConnectionStore()
            const routeName = router.currentRoute.value.name

            // For fields-connection, we don't have taskStore.field
            const isFieldsConnectionRoute = routeName === 'fields-connection' || routeName === 'fields-connection-customer'
            
            if (!taskStore.field && !isFieldsConnectionRoute) return

            if (colorChanged) {
                layer.color = color
                this.showColorPicker[layer.id] = false
            }

            switch (layer.id) {
                case 'field': {
                    if (isFieldsConnectionRoute) {
                        // On fields-connection route, we don't have taskStore.field
                        // For fields-connection, apply to all selected fields
                        if (fcs.startField_id) {
                            const startField = mapStore.fields.find(f => f.id === fcs.startField_id)
                            if (startField && startField.poly) {
                                startField.poly.setOptions({ fillColor: color })
                            }
                        }
                        if (fcs.endField_id) {
                            const endField = mapStore.fields.find(f => f.id === fcs.endField_id)
                            if (endField && endField.poly) {
                                endField.poly.setOptions({ fillColor: color })
                            }
                        }
                    } else {
                        // On normal route, we have taskStore.field
                        // Use the polygon from mapStore.fields
                        const mapField = mapStore.fields.find(f => f.id === taskStore.field.id)
                        
                        if (mapField && mapField.poly) {
                            mapField.poly.setOptions({ fillColor: color })
                        }
                    }
                    break
                }
                case 'outer_border': {
                    if (isFieldsConnectionRoute) {
                        // For fields-connection, apply to all selected fields
                        if (fcs.startField_id) {
                            const startField = mapStore.fields.find(f => f.id === fcs.startField_id)
                            if (startField && startField.poly) {
                                startField.poly.setOptions({ strokeColor: color })
                            }
                        }
                        if (fcs.endField_id) {
                            const endField = mapStore.fields.find(f => f.id === fcs.endField_id)
                            if (endField && endField.poly) {
                                endField.poly.setOptions({ strokeColor: color })
                            }
                        }
                    } else {
                        // Use the polygon from mapStore.fields
                        const mapField = mapStore.fields.find(f => f.id === taskStore.field.id)
                        
                        if (mapField && mapField.poly) {
                            mapField.poly.setOptions({ strokeColor: color })
                        }
                    }
                    break
                }
                case 'headland':
                    if (mapStore.draw.field.headlanes && mapStore.draw.field.headlanes.length > 0) {
                        mapStore.draw.field.headlanes.forEach((line) => {
                            line.setOptions({
                                strokeColor: color,
                                fillColor: color,
                            })
                        })
                    }
                    break
                case 'ab_lines':
                    if (mapStore.draw.field.abLines && mapStore.draw.field.abLines.length > 0) {
                        mapStore.draw.field.abLines.forEach((line) => {
                            line.setOptions({ strokeColor: color })
                        })
                    }
                    break
                case 'track_wheels':
                    if (mapStore.draw.field.trackWheels && mapStore.draw.field.trackWheels.length > 0) {
                        mapStore.draw.field.trackWheels.forEach((line) => {
                            line.setOptions({ strokeColor: color })
                        })
                    }
                    break
                case 'segments':
                    if (taskStore.field.segments && typeof taskStore.field.segments.setOptions === 'function') {
                        taskStore.field.segments.setOptions({ strokeColor: color })
                    }
                    break
            }
        },
        // Toggle visibility for NEW layers (in drawing mode)
        toggleNewLayerVisibility(layer) {
            const ds = useDrawingStore()
            const segmentsStore = useSegmentsStore()
            const fcs = useFieldsConnectionStore()
            const cs = useCustomers()
            const pls = useParkingLotsStore()
            // If layer is being hidden, close the color picker
            if (!layer.visible) {
                this.showColorPicker[layer.id] = false
            }

            const farmId = cs.selectedCustomers.length === 1 ? cs.selectedCustomers[0].id : null

            switch (layer.id) {
                case 'field':
                    if (ds.field.shapes && ds.field.shapes.length > 0) {
                        ds.field.shapes.forEach((shape) => {
                            toRaw(shape).setOptions({
                                fillOpacity: layer.visible ? 0.1 : 0,
                            })
                        })
                    }
                    break
                case 'outer_border':
                    if (ds.field.shapes && ds.field.shapes.length > 0) {
                        ds.field.shapes.forEach((shape) => {
                            toRaw(shape).setOptions({
                                strokeOpacity: layer.visible ? 1 : 0,
                            })
                        })
                    }
                    // For cutouts
                    if (ds.field.cutouts && ds.field.cutouts.length > 0) {
                        ds.field.cutouts.forEach((cutout) => {
                            toRaw(cutout).setOptions({
                                strokeOpacity: layer.visible ? 1 : 0,
                            })
                        })
                    }
                    break
                case 'headland':
                    if (ds.headlandPolygons && ds.headlandPolygons.length > 0) {
                        ds.headlandPolygons.forEach((item) => {
                            // Filter only headlands
                            if (item.type === 'headland') {
                                toRaw(item.polygon).setVisible(layer.visible)
                            }
                        })
                    }
                    break
                case 'ab_lines':
                    if (ds.abLinesPolylines && ds.abLinesPolylines.length > 0) {
                        ds.abLinesPolylines.forEach((polyline) => {
                            toRaw(polyline).setVisible(layer.visible)
                        })
                    }
                    if (ds.longestLinePolylines && ds.longestLinePolylines.length > 0) {
                        ds.longestLinePolylines.forEach((polyline) => {
                            toRaw(polyline).setVisible(layer.visible)
                        })
                    }
                    break
                case 'segments': {
                    if (segmentsStore.allSegmentsLines && segmentsStore.allSegmentsLines.length > 0) {
                        segmentsStore.allSegmentsLines.forEach((segment) => {
                            toRaw(segment).setVisible(layer.visible)
                        })
                    }
                    break
                }
                case 'track_wheels':
                    if (ds.wheelTrackLinesPolylines && ds.wheelTrackLinesPolylines.length > 0) {
                        ds.wheelTrackLinesPolylines.forEach((polyline) => {
                            // Wheel tracks use dashed line with icon, so we need to update icon strokeOpacity
                            toRaw(polyline).setOptions({
                                icons: [{
                                    icon: {
                                        path: 'M 0,0 0,1',
                                        strokeOpacity: layer.visible ? 1 : 0,
                                        scale: 4
                                    },
                                    offset: '0',
                                    repeat: '20px'
                                }]
                            })
                        })
                    }
                    break
                case 'farm_roi':
                    if (!farmId) return
    
                    if (layer.visible) {
                        fcs.getFarmROIs(farmId)
                    } else {
                        fcs.clearFarmROIs(farmId)
                    }
                    break
                case 'parking_lots':
                    if (layer.visible) {
                        if (!farmId) return
                        pls.loadParkingLots(farmId)
                    } else {
                        pls.clearParkingLots()
                    }
                    break
            }
        },
        // Update color for NEW layers (in drawing mode)
        updateNewLayerColor(layer, color, colorChanged = true) {
            const ds = useDrawingStore()
            const segmentsStore = useSegmentsStore()

            if (colorChanged) {
                layer.color = color
                this.showColorPicker[layer.id] = false
            }

            switch (layer.id) {
                case 'field':
                    if (ds.field.shapes && ds.field.shapes.length > 0) {
                        ds.field.shapes.forEach((shape) => {
                            shape.setOptions({ fillColor: color })
                        })
                    }
                    break
                case 'outer_border':
                    if (ds.field.shapes && ds.field.shapes.length > 0) {
                        ds.field.shapes.forEach((shape) => {
                            shape.setOptions({ strokeColor: color })
                        })
                    }
                    if (ds.field.cutouts && ds.field.cutouts.length > 0) {
                        ds.field.cutouts.forEach((cutout) => {
                            cutout.setOptions({ strokeColor: color })
                        })
                    }
                    break
                case 'headland':
                    if (ds.headlandPolygons && ds.headlandPolygons.length > 0) {
                        ds.headlandPolygons.forEach((item) => {
                            if (item.type === 'headland') {
                                toRaw(item.polygon).setOptions({
                                    strokeColor: color,
                                    fillColor: 'transparent',
                                })
                            }
                        })
                    }
                    break
                case 'ab_lines':
                    if (ds.abLinesPolylines && ds.abLinesPolylines.length > 0) {
                        ds.abLinesPolylines.forEach((polyline) => {
                            toRaw(polyline).setOptions({ strokeColor: color })
                        })
                    }
                    break
                case 'segments': {
                    if (segmentsStore.allSegmentsLines && segmentsStore.allSegmentsLines.length > 0) {
                        segmentsStore.allSegmentsLines.forEach((segment) => {
                            toRaw(segment).setOptions({ strokeColor: color })
                        })
                    }
                    break
                }
                case 'track_wheels':
                    if (ds.wheelTrackLinesPolylines && ds.wheelTrackLinesPolylines.length > 0) {
                        ds.wheelTrackLinesPolylines.forEach((polyline) => {
                            toRaw(polyline).setOptions({ strokeColor: color })
                        })
                    }
                    break
            }
        },
        layerVisibilityFollowsFieldPoly(layerId) {
            // don't follow the visibility of farm_roi and parking_lots layers
            return !layerId.startsWith('roi_') && layerId !== 'farm_roi' && layerId !== 'parking_lots'
        },
        // Close the dropdown, restore original colors and visibility when component unmounts
        restoreOriginalSettings() {
            const taskStore = useTasksStore()

            this.layers.forEach((layer) => {
                if (taskStore.field && taskStore.field.color !== null) {
                    this.updateLayerColor(layer, taskStore.field.color, false)
                }
                if (taskStore.field && this.layerVisibilityFollowsFieldPoly(layer.id)) {
                    layer.visible = layer.originalVisibility ?? false
                    this.toggleLayerVisibility(layer)
                }
            })
        },
        // Calculate the range for geometries (single mode = whole array, double mode = subset)
        getGeometryRange(type, geomIndices) {
            // For double mode
            if (geomIndices && geomIndices[type]) {
                return { start: geomIndices[type].start, end: geomIndices[type].end }
            }
            // For single mode
            const mapStore = useMaps()
            const arr = mapStore.draw.field[type]
            return { start: 0, end: arr?.length || 0 }
        },
        // Check if a layer has data for a field
        checkLayerHasDataForField(layerId, field, geomIndices = null) {
            if (!field) return false
            
            const mapStore = useMaps()
            const fcs = useFieldsConnectionStore()
            
            switch (layerId) {
                case 'field':
                case 'outer_border':
                    return !!field.poly
                case 'headland':
                    if (geomIndices) return geomIndices.headlanes?.start < geomIndices.headlanes?.end
                    return field.headland || mapStore.draw.field.headlanes?.length > 0
                case 'ab_lines':
                    if (geomIndices) return geomIndices.abLines?.start < geomIndices.abLines?.end
                    return field.ab_lines || mapStore.draw.field.abLines?.length > 0
                case 'segments':
                    if (geomIndices) return geomIndices.segments?.start < geomIndices.segments?.end
                    return field.headland_segments || mapStore.draw.field.segments?.length > 0
                case 'track_wheels':
                    return field.ab_track_lines?.coordinates?.length > 0 || mapStore.draw.field.trackWheels?.length > 0
                case 'roi_entrypoints': {
                    const fieldROIs = field?.id ? fcs.roisByFieldId?.[field.id] : fcs.generatedROIs
                    return fieldROIs?.some(roi => toRaw(roi).roiType === 'entrypoint') || false
                }
                case 'roi_obstacles': {
                    const fieldROIs = field?.id ? fcs.roisByFieldId?.[field.id] : fcs.generatedROIs
                    return fieldROIs?.some(roi => toRaw(roi).roiType === 'obstacle') || false
                }
                case 'roi_logistics': {
                    const fieldROIs = field?.id ? fcs.roisByFieldId?.[field.id] : fcs.generatedROIs
                    return fieldROIs?.some(roi => toRaw(roi).roiType === 'logistic') || false
                }
                case 'cutout': {
                    const fieldROIs = field?.id ? fcs.roisByFieldId?.[field.id] : fcs.generatedROIs
                    return fieldROIs?.some(roi => toRaw(roi).roiType === 'cutout') || false
                }
                default:
                    return false
            }
        },
        // Get the color for a layer on the map
        getLayerColorFromField(layerId, field, geomIndices = null) {
            const mapStore = useMaps()
            const range = geomIndices ? this.getGeometryRange(
                layerId === 'headland' ? 'headlanes' : layerId === 'ab_lines' ? 'abLines' : layerId,
                geomIndices
            ) : null
            
            switch (layerId) {
                case 'field':
                    return field.poly?.get?.('fillColor') || field.poly?.fillColor || field.color || '#808080'
                case 'outer_border':
                    return field.poly?.get?.('strokeColor') || field.poly?.strokeColor || '#000000'
                case 'headland': {
                    const idx = range ? range.start : 0
                    if (mapStore.draw.field.headlanes?.[idx]) {
                        const h = mapStore.draw.field.headlanes[idx]
                        return h?.get?.('strokeColor') || toRaw(h)?.strokeColor || '#40E0D0'
                    }
                    return '#40E0D0'
                }
                case 'ab_lines': {
                    const idx = range ? range.start : 0
                    if (mapStore.draw.field.abLines?.[idx]) {
                        const abLine = mapStore.draw.field.abLines[idx]
                        return abLine?.get?.('strokeColor') || toRaw(abLine)?.strokeColor || '#00FF00'
                    }
                    return '#00FF00'
                }
                case 'track_wheels': {
                    if (mapStore.draw.field.trackWheels?.[0]) {
                        const trackWheel = mapStore.draw.field.trackWheels[0]
                        return trackWheel?.get?.('strokeColor') || toRaw(trackWheel)?.strokeColor || '#00FFFF'
                    }
                    return '#00FFFF'
                }
                case 'segments':
                    return '#40E0D0'
                case 'roi_entrypoints':
                    return '#00FF00'
                case 'roi_obstacles':
                    return '#FF0000'
                case 'roi_logistics':
                    return '#0000FF'
                case 'cutout':
                    return '#FF00FF'
                default:
                    return '#000000'
            }
        },
        // Create layers for a field (used for single and double mode)
        // previousLayers: only for Field 1
        createLayersForField(field, fieldIndex, geomIndices = null, previousLayers = null) {
            const baseLayerIds = ['field', 'outer_border', 'headland', 'ab_lines', 'segments', 'track_wheels', 'roi_entrypoints', 'roi_obstacles', 'roi_logistics', 'cutout', 'farm_roi', 'parking_lots']
            const prevFarmRoi = previousLayers?.find((l) => l.id === 'farm_roi')
            const prevParkingLots = previousLayers?.find((l) => l.id === 'parking_lots')
            return baseLayerIds.map(id => {
                const baseLayer = this.layers.find(l => l.id === id)
                const hasData = this.checkLayerHasDataForField(id, field, geomIndices)
                const color = this.getLayerColorFromField(id, field, geomIndices)

                let visible
                if (id === 'track_wheels') {
                    visible = false
                } else if (id === 'farm_roi') {
                    visible = prevFarmRoi?.visible ?? false
                } else if (id === 'parking_lots') {
                    visible = prevParkingLots?.visible ?? false
                } else if (id.startsWith('roi_') || id === 'cutout') {
                    const prevRoiLayer = previousLayers?.find((l) => l.id === id)
                    visible = prevRoiLayer?.visible ?? hasData
                } else {
                    visible = hasData
                }

                return {
                    id: id,
                    label: baseLayer?.label || id,
                    visible,
                    color: color,
                    originalColor: color,
                    hasData: id === 'farm_roi' || id === 'parking_lots' ? true : hasData,
                    fieldIndex: fieldIndex
                }
            })
        },
        /** On singleField / field1 (not field2): for farm_roi ON + sync ROI */
        connectionLayersListContaining(layer) {
            if (this.singleFieldLayers?.includes(layer)) return this.singleFieldLayers
            if (this.field1Layers?.includes(layer)) return this.field1Layers
            return null
        },
        /** Field + farm if farm_roi is ON in the single/field1 list. Field2: only field layer */
        connectionRoiGeometriesForVisibility(field, layer) {
            const fcs = useFieldsConnectionStore()
            const cs = useCustomers()

            const out = []
            if (field?.id && fcs.roisByFieldId?.[field.id]) {
                out.push(...fcs.roisByFieldId[field.id])
            }
            const list = this.connectionLayersListContaining(layer)
            const farmId = cs.selectedCustomers.length === 1 ? cs.selectedCustomers[0].id : null
            const farmOn = list?.find((l) => l.id === 'farm_roi')?.visible
            if (farmOn && farmId && fcs.roisByFieldId?.[farmId]) {
                out.push(...fcs.roisByFieldId[farmId])
            }
            return out
        },
        // Apply visibility for a layer on geometries
        applyLayerVisibility(layer, field, geomIndices = null) {
            if (!field) return
            
            const mapStore = useMaps()
            const fcs = useFieldsConnectionStore()
            const cs = useCustomers()
            const pls = useParkingLotsStore()

            const opacity = layer.visible ? 1 : 0
            const fillOpacity = layer.visible ? 0.5 : 0
            const farmId = cs.selectedCustomers.length === 1 ? cs.selectedCustomers[0].id : null
            
            switch (layer.id) {
                case 'field':
                    if (field.poly) toRaw(field.poly).setOptions({ fillOpacity })
                    break
                case 'outer_border':
                    if (field.poly) toRaw(field.poly).setOptions({ strokeOpacity: opacity })
                    break
                case 'headland': {
                    const range = this.getGeometryRange('headlanes', geomIndices)
                    for (let i = range.start; i < range.end; i++) {
                        const headland = mapStore.draw.field.headlanes[i]
                        if (headland) toRaw(headland).setOptions({ strokeOpacity: opacity })
                    }
                    break
                }
                case 'ab_lines': {
                    const range = this.getGeometryRange('abLines', geomIndices)
                    for (let i = range.start; i < range.end; i++) {
                        const abLine = mapStore.draw.field.abLines[i]
                        if (abLine) toRaw(abLine).setOptions({ strokeOpacity: opacity })
                    }
                    break
                }
                case 'segments': {
                    const range = this.getGeometryRange('segments', geomIndices)
                    for (let i = range.start; i < range.end; i++) {
                        const segment = mapStore.draw.field.segments[i]
                        if (segment) toRaw(segment).setVisible(layer.visible)
                    }
                    break
                }
                case 'track_wheels': {
                    const range = this.getGeometryRange('trackWheels', geomIndices)
                    for (let i = range.start; i < range.end; i++) {
                        const trackWheel = mapStore.draw.field.trackWheels[i]
                        if (trackWheel) {
                            toRaw(trackWheel).setOptions({
                                icons: [{
                                    icon: {
                                        path: 'M 0,0 0,1',
                                        strokeOpacity: layer.visible ? 1 : 0,
                                        scale: 4
                                    },
                                    offset: '0',
                                    repeat: '20px'
                                }]
                            })
                        }
                    }
                    break
                }
                case 'roi_entrypoints': {
                    const rois = this.connectionRoiGeometriesForVisibility(field, layer)
                    rois.forEach((roi) => {
                        const rawROI = toRaw(roi)
                        if (rawROI.roiType !== 'entrypoint') return
                        if (!layer.visible) fcs.closeInfoWindowsForROI(rawROI)
                        if (typeof rawROI.setVisible === 'function') {
                            rawROI.setVisible(layer.visible)
                        } else if (typeof rawROI.setMap === 'function') {
                            rawROI.setMap(layer.visible ? toRaw(mapStore.map) : null)
                        } else {
                            rawROI.map = layer.visible ? toRaw(mapStore.map) : null
                        }
                    })
                    break
                }
                case 'roi_obstacles': {
                    const rois = this.connectionRoiGeometriesForVisibility(field, layer)
                    rois.forEach((roi) => {
                        const rawROI = toRaw(roi)
                        if (rawROI.roiType !== 'obstacle') return
                        if (!layer.visible) fcs.closeInfoWindowsForROI(rawROI)
                        if (typeof rawROI.setVisible === 'function') {
                            rawROI.setVisible(layer.visible)
                        } else if (typeof rawROI.setMap === 'function') {
                            rawROI.setMap(layer.visible ? toRaw(mapStore.map) : null)
                        } else {
                            rawROI.map = layer.visible ? toRaw(mapStore.map) : null
                        }
                    })
                    break
                }
                case 'roi_logistics': {
                    const rois = this.connectionRoiGeometriesForVisibility(field, layer)
                    rois.forEach((roi) => {
                        const rawROI = toRaw(roi)
                        if (rawROI.roiType !== 'logistic') return
                        if (!layer.visible) fcs.closeInfoWindowsForROI(rawROI)
                        if (typeof rawROI.setMap === 'function') {
                            rawROI.setMap(layer.visible ? toRaw(mapStore.map) : null)
                        } else {
                            rawROI.map = layer.visible ? toRaw(mapStore.map) : null
                        }
                    })
                    break
                }
                case 'cutout': {
                    const rois = this.connectionRoiGeometriesForVisibility(field, layer)
                    rois.forEach((roi) => {
                        const rawROI = toRaw(roi)
                        if (rawROI.roiType !== 'cutout') return
                        if (!layer.visible) fcs.closeInfoWindowsForROI(rawROI)
                        if (typeof rawROI.setMap === 'function') {
                            rawROI.setMap(layer.visible ? toRaw(mapStore.map) : null)
                        } else {
                            rawROI.map = layer.visible ? toRaw(mapStore.map) : null
                        }
                    })
                    break
                }
                case 'farm_roi': {
                    if (!farmId) return

                    const connectionList = this.connectionLayersListContaining(layer)

                    if (layer.visible) {
                        this.getFarmROIsAndSyncRoiToggles(farmId, connectionList)
                    } else {
                        fcs.clearFarmROIs(farmId)
                        if (connectionList) {
                            this.syncListRoiSubLayersFromGeneratedRois(connectionList)
                        }
                    }
                    break
                }
                case 'parking_lots': {
                    if (layer.visible) {
                        if (!farmId) return
                        pls.loadParkingLots(farmId)
                    } else {
                        pls.clearParkingLots()
                    }
                    break
                }
            }
        },
        // Apply color for a layer on geometries
        applyLayerColor(layer, color, field, geomIndices = null) {
            if (!field) return
            
            const mapStore = useMaps()
            layer.color = color
            
            switch (layer.id) {
                case 'field':
                    if (field.poly) toRaw(field.poly).setOptions({ fillColor: color })
                    break
                case 'outer_border':
                    if (field.poly) toRaw(field.poly).setOptions({ strokeColor: color })
                    break
                case 'headland': {
                    const range = this.getGeometryRange('headlanes', geomIndices)
                    for (let i = range.start; i < range.end; i++) {
                        const headland = mapStore.draw.field.headlanes[i]
                        if (headland) toRaw(headland).setOptions({ strokeColor: color })
                    }
                    break
                }
                case 'ab_lines': {
                    const range = this.getGeometryRange('abLines', geomIndices)
                    for (let i = range.start; i < range.end; i++) {
                        const abLine = mapStore.draw.field.abLines[i]
                        if (abLine) toRaw(abLine).setOptions({ strokeColor: color })
                    }
                    break
                }
            }
        },
        // Apply all colors from layers on geometries
        applyAllLayersColors(layers, field, geomIndices = null) {
            if (!field || !layers) return
            
            layers.forEach(layer => {
                if (!layer.hasData || !layer.color) return
                this.applyLayerColor(layer, layer.color, field, geomIndices)
            })
        },
        /** Polylines: can have map but setVisible(false)
         *  Markers: .map (but not setVisible(false))
         *  After clear: setMap(null) -> this.clearFarmROIs(farmId)
         */
        checkRoiVisibleOnMap(roi) {
            const r = toRaw(roi)
            const onMap = typeof r.getMap === 'function' ? r.getMap() != null : r.map != null
            if (!onMap) return false
            return typeof r.getVisible !== 'function' || r.getVisible() !== false
        },
        syncListRoiSubLayersFromGeneratedRois(list) {
            if (!list?.length) return
            const fcs = useFieldsConnectionStore()
            const mapStore = useMaps()
            const cs = useCustomers()

            // field1 / single uses fields[0]; field2 uses fields[1] (otherwise scope ROIs is on the wrong field)
            const field = list === this.field2Layers ? mapStore.draw.fields?.[1] : mapStore.draw.fields?.[0]
            const farmId = cs.selectedCustomers.length === 1 ? cs.selectedCustomers[0].id : null
            const farmOn = list.find((l) => l.id === 'farm_roi')?.visible
            // Get ROIs by field
            const byField = field?.id ? fcs.roisByFieldId?.[field.id] : null
            // Get ROIs by farm
            const byFarm = farmOn && farmId ? fcs.roisByFieldId?.[farmId] : null
            // Get all ROIs in scope
            const scopeRois = (byField ?? []).concat(byFarm ?? [])

            // Sync each ROI sub layer
            for (const [layerId, roiType] of this.roiSubLayerPairs) {
                const sub = list.find((l) => l.id === layerId)
                if (!sub) continue
                // Get ROIs of type for the sub layer
                const ofType = scopeRois.filter((r) => toRaw(r).roiType === roiType).map((r) => toRaw(r))
                // Check if there are any ROIs of type in scope
                sub.hasData = ofType.length > 0
                // If no ROIs of type in scope, set visible to false
                if (!sub.hasData) {
                    sub.visible = false
                } else {
                    // Check if any ROI is visible on the map
                    sub.visible = ofType.some((r) => this.checkRoiVisibleOnMap(r))
                }
            }
        },
        async getFarmROIsAndSyncRoiToggles(farmId, connectionList) {
            const fcs = useFieldsConnectionStore()
            await fcs.getFarmROIs(farmId)
            if (connectionList) {
                this.syncListRoiSubLayersFromGeneratedRois(connectionList)
            }
        },
        resetSingleFieldLayers() {
            this.singleFieldLayers = null
        },
        initializeSingleFieldLayers() {
            const mapStore = useMaps()
            const field = mapStore.draw.fields?.[0]
            const previous = this.singleFieldLayers

            if (!field) {
                this.singleFieldLayers = []
                return
            }

            this.singleFieldLayers = this.createLayersForField(field, 0, field.drawnGeometries, previous)
            this.applyAllLayersColors(this.singleFieldLayers, field, field.drawnGeometries)
        },
        toggleSingleFieldLayerVisibility(layer) {
            const mapStore = useMaps()
            const field = mapStore.draw.fields?.[0]
            if (!field) return

            this.applyLayerVisibility(layer, field, field.drawnGeometries)
        },
        updateSingleFieldLayerColor(layer, color) {
            const mapStore = useMaps()
            const field = mapStore.draw.fields?.[0]
            if (!field) return

            this.applyLayerColor(layer, color, field, field.drawnGeometries)
        },
        shuffleSingleFieldSegmentColors() {
            const mapStore = useMaps()
            if (!mapStore.draw.field.segments?.length) return
            mapStore.shuffleSegmentColors()
        },
        resetFieldConnectionLayers() {
            this.field1Layers = null
            this.field2Layers = null
        },
        initializeField1Layers() {
            const mapStore = useMaps()
            const field = mapStore.draw.fields?.[0]
            const previous = this.field1Layers
            if (field && field.drawnGeometries) {
                this.field1Layers = this.createLayersForField(field, 0, field.drawnGeometries, previous)
                this.applyAllLayersColors(this.field1Layers, field, field.drawnGeometries)
            } else {
                this.field1Layers = []
            }
        },
        initializeField2Layers() {
            const mapStore = useMaps()
            const field = mapStore.draw.fields?.[1]
            const previous = this.field2Layers
            if (field && field.drawnGeometries) {
                this.field2Layers = this.createLayersForField(field, 1, field.drawnGeometries, previous)
                this.applyAllLayersColors(this.field2Layers, field, field.drawnGeometries)
            } else {
                this.field2Layers = []
            }
        },
        toggleFieldLayerVisibility(layer, fieldIndex) {
            const mapStore = useMaps()
            const field = mapStore.draw.fields?.[fieldIndex]
            if (!field || !field.drawnGeometries) return

            this.applyLayerVisibility(layer, field, field.drawnGeometries)
        },
        updateFieldLayerColor(layer, color, fieldIndex) {
            const mapStore = useMaps()
            const field = mapStore.draw.fields?.[fieldIndex]
            if (!field || !field.drawnGeometries) return

            this.applyLayerColor(layer, color, field, field.drawnGeometries)
        },
        shuffleFieldSegmentColors(fieldIndex) {
            const mapStore = useMaps()
            const field = mapStore.draw.fields?.[fieldIndex]
            if (!field || !field.drawnGeometries) return
            
            const geom = field.drawnGeometries
            mapStore.randomRainbowColors = [...mapStore.randomRainbowColors].sort(() => Math.random() - 0.5)
            
            for (let i = geom.segments.start; i < geom.segments.end; i++) {
                const segment = mapStore.draw.field.segments[i]
                if (segment) {
                    const colorIndex = i - geom.segments.start
                    toRaw(segment).setOptions({
                        strokeColor: mapStore.randomRainbowColors[colorIndex % mapStore.randomRainbowColors.length]
                    })
                }
            }
        },
        toggleFieldLayersVisibility(visible) {
            const ts = useTasksStore()
            const ms = useMaps()

            if (!ts.field) return
            // Layers which are toggled when showing yield map/iso preview
            const toggleLayers = ['ab_lines', 'field']

            toggleLayers.forEach(layerId => {
                const layer = this.layers.find(layer => layer.id === layerId)
                if (layer) {
                    layer.visible = visible
                    this.toggleLayerVisibility(layer)
                }
            })

            const mapField = ms.fields.find((field) => field.id === ts.field.id)
            if (mapField?.poly) {
                toRaw(mapField.poly).setOptions({
                    clickable: visible,
                })
            }
        }
    },
})

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useFieldLayersStore, import.meta.hot))
}
